/**
 * @file TaskSearchInput.jsx
 * @description Task search component that only accepts numeric input and navigates to the task details page.
 */

import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { getTaskByNumber } from "../../firebase/tasks";

export default function TaskSearchInput() {
  const [inputValue, setInputValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const [notFoundError, setNotFoundError] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const history = useHistory();

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setNotFoundError(""); // Clear previous "not found" error

    if (value === "") {
      setValidationError("");
    } else if (!/^\d+$/.test(value)) {
      setValidationError("Only numbers are allowed");
    } else {
      setValidationError("");
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    if (!inputValue) {
      setValidationError("Please enter a task number");
      return;
    }

    if (!/^\d+$/.test(inputValue)) {
      setValidationError("Only numbers are allowed");
      return;
    }

    const taskNum = Number(inputValue);
    setIsSearching(true);
    setNotFoundError("");
    setValidationError("");

    try {
      const task = await getTaskByNumber(taskNum);
      if (task) {
        setInputValue("");
        history.push(`/tasks/${taskNum}`);
      } else {
        setNotFoundError(`No task found for ${inputValue}`);
      }
    } catch (error) {
      console.error("Error searching task:", error);
      setNotFoundError("Error querying database");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="w-full md:w-64 flex flex-col">
      <form onSubmit={handleSearchSubmit} className="relative flex items-center">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Search by task #..."
          className="w-full rounded-xl pl-4 pr-10 py-2 text-sm font-medium focus:outline-none focus:ring-2 transition-all duration-200"
          style={{
            backgroundColor: "var(--theme-input-bg)",
            border: "1px solid var(--theme-input-border)",
            borderRadius: "var(--theme-input-radius)",
            color: "var(--theme-color-ink)",
          }}
          disabled={isSearching}
        />
        <button
          type="submit"
          disabled={isSearching}
          className="absolute right-2 p-1 focus:outline-none transition-colors duration-200"
          style={{ color: "var(--theme-color-body-mid)" }}
          aria-label="Search task"
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--theme-color-primary)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--theme-color-body-mid)")}
        >
          {isSearching ? (
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              style={{ color: "var(--theme-color-primary)" }}
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          )}
        </button>
      </form>

      {validationError && (
        <span
          className="text-xs mt-1 pl-1 font-medium"
          style={{ color: "var(--theme-color-error, #dc3545)" }}
        >
          {validationError}
        </span>
      )}

      {notFoundError && (
        <div
          className="text-xs mt-1.5 px-3 py-1.5 rounded-lg font-medium shadow-sm transition-all duration-200"
          style={{
            backgroundColor: "var(--theme-color-canvas-soft)",
            border: "1px solid var(--theme-color-mute)",
            color: "var(--theme-color-body)",
          }}
        >
          {notFoundError}
        </div>
      )}
    </div>
  );
}
